import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getSearchBoxValues } from 'modules/searchBox/selectors';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Text } from '@daedalus/atlas/Text';
import { defaultCurrencyCode } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { OfferPrice } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice } from '@daedalus/core/src/price/business/price';
import { roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomWithoutOffers, SplitBookingDates } from '@daedalus/core/src/room/types/room';
import { calculateDateDifference } from '@daedalus/core/src/utils/date';
import PriceDetails from '@daedalus/shared/src/booking/PriceDetailsV2';
import { RoomPrice } from '@daedalus/shared/src/booking/RoomDetailsSection';
export interface PriceBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefersReducedMotion?: boolean;
  prices: OfferPrice[];
  splitBookingRooms: RoomWithoutOffers[];
  splitBookingDates: SplitBookingDates;
  propertyCurrencyCode: string | undefined;
}
const Title = () => <Text variant="titleM">
    <FormattedMessageWrapper id="priceBreakdown" defaultMessage="Price Breakdown" />
  </Text>;
const _exp = /*#__PURE__*/() => Grid;
const RoomNamesWrapper = /*#__PURE__*/styled(_exp())({
  name: "RoomNamesWrapper",
  class: "r1uqwlx9",
  propsAsIs: true
});
const _exp3 = /*#__PURE__*/() => ContentWrapper;
const DesktopContentWrapper = /*#__PURE__*/styled(_exp3())({
  name: "DesktopContentWrapper",
  class: "drwojal",
  propsAsIs: true
});
export const PriceBreakdownModal = ({
  isOpen = false,
  onClose,
  prefersReducedMotion,
  prices,
  splitBookingDates,
  splitBookingRooms,
  propertyCurrencyCode
}: PriceBreakdownModalProps) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    rooms
  } = useSelector(getSearchBoxValues);
  const roomsConfig = roomsToConfigurationObject(rooms);
  const firstRoomNumberOfNights = calculateDateDifference(new Date(splitBookingDates.checkIn), new Date(splitBookingDates.splitDate));
  const secondRoomNumberOfNights = calculateDateDifference(new Date(splitBookingDates.splitDate), new Date(splitBookingDates.checkOut));
  const roomsNightsAmount = [firstRoomNumberOfNights, secondRoomNumberOfNights];
  const totalNumberOfNights = firstRoomNumberOfNights + secondRoomNumberOfNights;
  const displayPrice = getDisplayPrice(prices);
  const {
    currencyCode: displayCurrencyCode,
    base: baseRateDisplayPrice
  } = displayPrice.chargeable;
  const currencyCode = displayCurrencyCode || defaultCurrencyCode;
  const roomPricePerNight = baseRateDisplayPrice / totalNumberOfNights;
  const Content = () => <>
      <RoomNamesWrapper container spacing="s000" gap="s400" direction="column">
        {splitBookingRooms.map((room, index) => {
        const numberOfNights = roomsNightsAmount[index];
        return <div data-id={`splitBooking.roomNames.${index}`} key={index}>
              <Text variant="bodyM" as="span">
                {`1x ${room.name}`} <span>·</span>&nbsp;
              </Text>
              <Text variant="bodyM" as="span">
                <FormattedMessageWrapper id="splitBooking.numberOfNights" defaultMessage="{numberOfNights} {numberOfNights, plural, one {night} other {nights}}" values={{
              numberOfNights
            }} />
              </Text>
            </div>;
      })}
      </RoomNamesWrapper>
      <ContentWrapper paddingY="s500">
        <Divider />
      </ContentWrapper>
      <div data-id="splitBooking.roomPriceBreakdown">
        <RoomPrice currencyCode={currencyCode} numberOfNights={totalNumberOfNights} numberOfRooms={1} roomPricePerNight={roomPricePerNight} totalPricePerRoom={baseRateDisplayPrice} includeNumberOfRooms={false} isLoading={false} showAverageNightlyPriceText />
      </div>
      <PriceDetails showFeesDescriptions={false} showCurrencySelector={false} checkIn={splitBookingDates.checkIn} checkOut={splitBookingDates.checkOut} prices={prices} numberOfRooms={roomsConfig.numberOfRooms} numberOfGuests={roomsConfig.numberOfAdults + roomsConfig.numberOfChildren} displayNumberOfRooms={false} propertyCurrencyCode={propertyCurrencyCode} />
    </>;
  if (isMobile) return <BottomSheet isOpen={isOpen} titleBarProps={{
    centerContent: <Title />
  }} onClose={onClose} prefersReducedMotion={prefersReducedMotion}>
        <ContentWrapper paddingTop="s500">
          <Content />
        </ContentWrapper>
      </BottomSheet>;
  return <PopUp titleBarProps={{
    centerContent: <Title />
  }} isOpen={isOpen} closeAction={onClose} width={600}>
      <DesktopContentWrapper padding="s000">
        <Content />
      </DesktopContentWrapper>
    </PopUp>;
};

require("./PriceBreakdownModal.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PriceBreakdownModal.tsx");